import { Inject, Injectable, InjectionToken, Optional, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser, isPlatformServer, PlatformLocation } from '@angular/common';
import { Meta } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { REQUEST, RESPONSE } from '@nguniversal/express-engine/tokens';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { DirectionService } from '../services/direction.service';
import { addBanglaDigits, BanglaDigitsConfig, banglaDigitsRegular } from './bangla-digits';
import {
    CultureDictionary, CultureLanguage, DICTIONARY_TIME_LIMIT_MS, ENGLISH, fileKey, langOfPath, languageInfo, LOCALE_IDS,
    categoryKey, formatLabel, pageLanguage, pathInLang, pathWithoutLang, setKeys, translate, usesSellerSet,
} from './culture.config';

/** Where the server render gets its dictionaries (culture-server.ts): from memory, refreshed from the published files (12.3). */
export interface CultureSource {
    /** The set's dictionary and its file's address, or null - no manifest yet, no such file, or too slow. */
    load(lang: string, keys: string[]): Promise<{ file: string, dict: CultureDictionary } | null>;
    /** The languages as the manifest has them, or null before there is one. */
    languages(): CultureLanguage[] | null;
}

export const CULTURE_SOURCE = new InjectionToken<CultureSource>('CULTURE_SOURCE');

/** og:locale for a page in each language (7); English's is the protocol's default and is not written. */
const OG_LOCALES: { [lang: string]: string } = { ar: 'ar_AE', es: 'es_ES', bn: 'bn_BD' };

/**
 * The page's language and its labels (Multilang_Culture_Spec.md 5.1). The language is the address's prefix when it has one,
 * else the visitor's choice (the culture cookie), else English (pageLanguage) - fixed for the page load: the language
 * switch sets the cookie and loads the page again, at the same address.
 *
 * The labels: the server render takes the dictionary from its own memory (CultureSource), so the HTML is already in the
 * language, and names the file in the head - a culture-file meta and a preload, so the browser fetches it alongside the
 * bundles. In the browser an APP_INITIALIZER waits for that file before the app's first draw (Angular 11 redraws the whole
 * page over the server's, so drawing first would flash English), at most 3 seconds; after that, or on any failure, the
 * page draws with the English written inline. A page drawn without a server render (the account area) finds its file in
 * manifest.json. No TransferState copy, no localStorage: the file is cached by the browser for good.
 *
 * An English page loads no dictionary at all: every label's English is written inline beside its key (culture-check holds
 * them equal), and nobody edits English but the code - so an English page never waits on, or depends on, the files.
 */
@Injectable({
    providedIn: 'root'
})
export class CultureService {
    /** The host's languages, English first (environment.cultureLanguages). */
    readonly languages: string[] = ((environment as any).cultureLanguages || [ENGLISH]).slice();

    /** The host's storefront app keys, in load order (environment.cultureAppKeys). */
    readonly appKeys: string[] = ((environment as any).cultureAppKeys || ['core']).slice();

    readonly lang: string;

    /** The language the address's prefix names (English when it has none): app links are written in it (link()). */
    readonly urlLang: string;

    dir: 'ltr' | 'rtl';

    /** The labels: {lang}_{labelid} to text. */
    dict: CultureDictionary = {};

    /** The dictionary file this page uses, or null (English inline). */
    file: string = null;

    /** The languages as the server published them (server render only), else null. */
    published: CultureLanguage[] = null;

    private loadedSets: { [set: string]: boolean } = {};
    /** Each translated text this page showed, to the English it came from (english()). */
    private englishOf = new Map<string, string>();
    private logged: { [what: string]: boolean } = {};

    constructor(@Inject(DOCUMENT) private document: any, @Inject(PLATFORM_ID) private platformId: any,
                private location: PlatformLocation, private direction: DirectionService, private meta: Meta,
                @Optional() @Inject(CULTURE_SOURCE) private source: CultureSource,
                @Optional() @Inject(RESPONSE) private response: any,
                @Optional() @Inject(REQUEST) private request: any) {
        this.urlLang = langOfPath(location.pathname, this.languages);
        this.lang = pageLanguage(location.pathname, this.cookies(), this.languages);
        this.dir = languageInfo(this.lang).direction;
    }

    /** The visitor's cookies: the request's on the server render, the document's in the browser. */
    private cookies(): string {
        if (isPlatformServer(this.platformId)) {
            return (this.request && this.request.headers && this.request.headers.cookie) || '';
        }
        try { return (this.document && this.document.cookie) || ''; } catch { return ''; }
    }

    /** A label: the page language's text for key, else fallback (the English, written inline), {name} filled from params. */
    t(key: string, fallback: string, params?: { [name: string]: any }): string {
        const text = translate(this.dict, this.lang, key, fallback, params, dictKey => this.logOnce('placeholders:' + dictKey,
            'the label ' + dictKey + ' has other placeholders than its English; the English is shown'));
        if (this.lang !== ENGLISH && fallback && text !== fallback && this.englishOf.size < 5000) {
            this.englishOf.set(text, formatLabel(fallback, params));
        }
        return text;
    }

    /** The English a text this page showed was translated from, else the text: for what is recorded in one language (analytics). */
    english(text: string): string {
        return (text && this.englishOf.get(text)) || text;
    }

    /** A category's name in the page's language (its label, categoryKey), else as the shop stores it. */
    category(name: string): string {
        return name ? this.t(categoryKey(name), name) : name;
    }

    /**
     * An app link as this address writes it: on a page under a prefix ('/ar/blog/x') '/account/cart' is '/ar/account/cart';
     * on every other page - a chosen language included - the link stays as written. Arrays: their first part.
     */
    link<T extends string | any[]>(link: T): T {
        if (Array.isArray(link)) {
            if (!link.length || typeof link[0] !== 'string') { return link; }
            return [pathInLang(link[0], this.urlLang, this.languages), ...link.slice(1)] as T;
        }
        return (typeof link === 'string' ? pathInLang(link, this.urlLang, this.languages) : link) as T;
    }

    /**
     * The same page for the switch: its address without a prefix - the switch sets the visitor's language (the cookie), and
     * every address without a prefix is shown in it. Every language goes to the same address. Without its #part: a link to
     * the same address with one only scrolls, and the page would not load again in the new language.
     */
    urlIn(lang: string, url: string): string {
        return pathWithoutLang(String(url || '/').split('#')[0] || '/', this.languages);
    }

    /** A URL without this page's language prefix, for rules written for English paths. */
    bare(url: string): string {
        return pathWithoutLang(url, this.languages);
    }

    info(code: string = this.lang): CultureLanguage {
        return languageInfo(code, this.published);
    }

    /** The APP_INITIALIZER: the page's set loaded (or English), then lang, dir and the language's fonts on the document. */
    init(): Promise<void> {
        const keys = setKeys(this.appKeys, usesSellerSet(this.location.pathname, this.languages));
        const work = this.lang === ENGLISH ? this.englishOnly()
            : isPlatformServer(this.platformId) ? this.loadOnServer(keys) : this.loadInBrowser(keys);
        return work
            .catch(err => this.logOnce('init', 'the labels could not be loaded; the page is in English', err))
            .then(() => this.applyToDocument());
    }

    /**
     * The dashboard's set (core, the host's, seller) for a page of the account area reached inside the app: its labels
     * loaded before the page draws (the account route's guard), at most 3 seconds. Resolves either way.
     */
    loadSellerSet(): Promise<boolean> {
        const keys = setKeys(this.appKeys, true);
        if (this.lang === ENGLISH || this.loadedSets[keys.join(',')] || isPlatformServer(this.platformId)) { return Promise.resolve(true); }
        const deadline = Date.now() + DICTIONARY_TIME_LIMIT_MS;
        return this.fileFromManifest(keys, deadline)
            .then(file => file ? this.fetchJson(file, deadline) : null)
            .then(dict => {
                if (dict) { this.use(dict, keys); }
                return true;
            }, () => true);
    }

    /**
     * After every navigation the server renders: a page in a language search engines may not index yet (13) says so -
     * noindex, follow - unless the page is real content in that language (route data cultureContent: a SitePage or a
     * post written in it), which keeps the standing it had. The header is what crawlers obey; the meta tag is for anyone
     * reading the page. In the browser: a navigation to a page in another language is a full page load (followLanguage).
     */
    watch(router: Router): void {
        if (isPlatformBrowser(this.platformId)) {
            this.followLanguage(router);
            return;
        }
        if (!isPlatformServer(this.platformId)) { return; }
        router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
            if (this.lang === ENGLISH || this.info().indexable || CultureService.ownContent(router.routerState.snapshot.root)) { return; }
            if (this.response && typeof this.response.setHeader === 'function') {
                this.response.setHeader('X-Robots-Tag', 'noindex, follow');
            }
            this.meta.updateTag({ name: 'robots', content: 'noindex, follow' });
        });
    }

    /**
     * A link inside the app stays in the app when its page is in the page's language (pageLanguage: a visitor who chose
     * Bangla reads every address without a prefix in Bangla, so every ordinary link stays in Bangla); a link whose page is
     * in another language - another prefix, or, on a page whose language came from its prefix, an address without one for
     * a visitor who chose none - is a full page load, as the switch is, since the labels are the page load's.
     */
    private followLanguage(router: Router): void {
        router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((e: NavigationStart) => {
            if (pageLanguage(e.url, this.cookies(), this.languages) === this.lang) { return; }
            const nav = typeof router.getCurrentNavigation === 'function' ? router.getCurrentNavigation() : null;
            const extras = (nav && nav.extras) || {};
            // A page shown without changing the address (a not-found in place) stays in the app, in this page's language.
            if (extras.skipLocationChange) { return; }
            // A redirect takes the place of the address it came from, as the router would have: Back does not land on it again.
            this.loadPage(e.url, !!extras.replaceUrl || !nav || !nav.previousNavigation);
        });
    }

    /**
     * This page asks search engines not to index it (noindex, follow): the header crawlers obey, on the server render, and
     * the robots meta tag. For a page under a language prefix with nothing of its own in that language - a product with no
     * title in it (Multilang_Culture_Spec.md 7).
     */
    noIndex(): void {
        if (isPlatformServer(this.platformId) && this.response && typeof this.response.setHeader === 'function') {
            this.response.setHeader('X-Robots-Tag', 'noindex, follow');
        }
        this.meta.updateTag({ name: 'robots', content: 'noindex, follow' });
    }

    /** A full page load of an app address; replace: in place of the current history entry. */
    protected loadPage(url: string, replace = false): void {
        if (replace) { this.document.location.replace(url); } else { this.document.location.assign(url); }
    }

    private static ownContent(route: ActivatedRouteSnapshot): boolean {
        for (let r = route; r; r = r.firstChild) {
            if (r.routeConfig && r.routeConfig.data && r.routeConfig.data.cultureContent) { return true; }
        }
        return false;
    }

    /** English: the inline text is the page's; the server render still learns the languages (the switch, noindex). */
    private async englishOnly(): Promise<void> {
        if (isPlatformServer(this.platformId) && this.source) {
            this.published = this.source.languages();
        }
    }

    private async loadOnServer(keys: string[]): Promise<void> {
        if (!this.source) { return; }
        const loaded = await this.source.load(this.lang, keys);
        this.published = this.source.languages();
        if (loaded) {
            this.use(loaded.dict, keys);
            this.file = loaded.file;
            this.nameFileInHead(loaded.file);
        } else if (this.response && typeof this.response.setHeader === 'function') {
            // No dictionary at all (a fresh server with nothing published): the page is in English, the head names no
            // file so the browser draws English too, and no cache keeps this page (12.3).
            this.response.setHeader('Cache-Control', 'no-store');
        }
    }

    private async loadInBrowser(keys: string[]): Promise<void> {
        const deadline = Date.now() + DICTIONARY_TIME_LIMIT_MS;
        const named = this.document.querySelector('meta[name="culture-file"]');
        const file = (named && named.getAttribute('content')) || await this.fileFromManifest(keys, deadline);
        if (!file) {
            if (this.lang !== ENGLISH) { this.logOnce('no-file', 'no dictionary file for ' + fileKey(this.lang, keys) + '; the page is in English'); }
            return;
        }
        const dict = await this.fetchJson(file, deadline);
        if (dict) {
            this.use(dict, keys);
            this.file = file;
        } else {
            this.logOnce('file', 'the dictionary ' + file + ' did not load within 3 s; the page is in English');
        }
    }

    private use(dict: CultureDictionary, keys: string[]): void {
        this.dict = Object.assign({}, this.dict, dict || {});
        // A larger set holds the smaller ones: the seller set is the storefront's too.
        for (let n = 1; n <= keys.length; n++) { this.loadedSets[keys.slice(0, n).join(',')] = true; }
    }

    private async fileFromManifest(keys: string[], deadline: number): Promise<string> {
        const manifest = await this.fetchJson('/culture/manifest.json', deadline, true);
        const files = manifest && manifest.files;
        return files ? files[fileKey(this.lang, keys)] || null : null;
    }

    /** JSON from the page's own origin, or null after the deadline or on any failure. fetch, not HttpClient: the preload in the head is a fetch preload, and the interceptors would change the request. */
    private fetchJson(url: string, deadline: number, fresh = false): Promise<any> {
        const remaining = deadline - Date.now();
        if (remaining <= 0 || !isPlatformBrowser(this.platformId) || typeof fetch !== 'function') { return Promise.resolve(null); }
        return new Promise(resolve => {
            let settled = false;
            const finish = (value: any) => { if (!settled) { settled = true; clearTimeout(timer); resolve(value); } };
            const timer = setTimeout(() => finish(null), remaining);
            fetch(url, fresh ? { cache: 'no-cache' } : {})
                .then(res => res.ok ? res.json() : null)
                .then(finish, () => finish(null));
        });
    }

    /** The head names the page's file, for the browser's initializer, and preloads it next to the bundles (5.1). */
    private nameFileInHead(file: string): void {
        const head = this.document.head;
        const meta = this.document.createElement('meta');
        meta.setAttribute('name', 'culture-file');
        meta.setAttribute('content', file);
        head.appendChild(meta);
        const link = this.document.createElement('link');
        link.setAttribute('rel', 'preload');
        link.setAttribute('as', 'fetch');
        link.setAttribute('crossorigin', '');
        link.setAttribute('href', file);
        head.appendChild(link);
    }

    /** <html lang dir>, the theme's direction, og:locale, the language's own font and, for Bangla, its digits (8). English pages are left as index.html has them. */
    private applyToDocument(): void {
        this.dir = this.info().direction === 'rtl' ? 'rtl' : 'ltr';
        const html = this.document.documentElement;
        if (html.getAttribute('lang') !== this.lang) { html.setAttribute('lang', this.lang); }
        if (html.getAttribute('dir') !== this.dir) { html.setAttribute('dir', this.dir); }
        this.direction.value = this.dir;
        if (OG_LOCALES[this.lang]) {
            this.meta.updateTag({ property: 'og:locale', content: OG_LOCALES[this.lang] });
        }
        this.preloadFonts(((environment as any).cultureFonts || {})[this.lang] || []);
        const digits: BanglaDigitsConfig = (environment as any).banglaDigits;
        if (this.lang === 'bn' && digits) {
            addBanglaDigits(this.document, digits);
            this.preloadFonts([banglaDigitsRegular(digits)]);
        }
    }

    /** The language's font files, fetched with the page instead of after its CSS has laid the text out: no late swap. */
    private preloadFonts(files: string[]): void {
        for (const file of files) {
            if (this.document.head.querySelector(`link[rel="preload"][href="${file}"]`)) { continue; }
            const link = this.document.createElement('link');
            link.setAttribute('rel', 'preload');
            link.setAttribute('as', 'font');
            link.setAttribute('type', /[.]otf$/i.test(file) ? 'font/otf' : 'font/woff2');
            link.setAttribute('crossorigin', '');
            link.setAttribute('href', file);
            this.document.head.appendChild(link);
        }
    }

    /** Once per page load and thing: the console is quiet in production, so this is for the developer tools and the render server's log. */
    private logOnce(what: string, message: string, err?: any): void {
        if (this.logged[what]) { return; }
        this.logged[what] = true;
        console.error('[culture] ' + message, err || '');
    }
}

/** The APP_INITIALIZER factory. */
export function initCulture(culture: CultureService): () => Promise<void> {
    return () => culture.init();
}

/** LOCALE_ID from the page's language (5.4): its prefix, else the visitor's choice. */
export function localeIdFor(culture: CultureService): string {
    return LOCALE_IDS[culture.lang] || LOCALE_IDS[ENGLISH];
}
